// The identity UI (account domain) and the dashboard hold independent
// sessions: an active identity session doesn't imply the dashboard has one.
// This module owns the redirect contract between them — identity-ui builds
// auto-login URLs, the dashboard login route consumes the params.

export const AUTO_LOGIN_PARAM = "auto_login";
export const RETURN_TO_PARAM = "return_to";
export const PROMPT_PARAM = "prompt";

/**
 * OIDC prompt asking the provider for its registration screen instead of
 * login. Keeps account creation inside the OAuth2 login challenge, so the
 * new account is signed in when the flow completes — no second login.
 */
export const REGISTRATION_PROMPT = "registration";

/**
 * Build the redirect that hands a user over to the dashboard. An identity
 * session doesn't imply a dashboard session, so dashboard-bound targets are
 * routed through the dashboard login with auto sign-in: the OIDC handshake
 * runs immediately instead of stranding the user on the login page. Pass
 * `prompt` to start the flow on a specific provider screen (registration).
 * Non-dashboard targets handle their own authentication and are returned
 * unchanged.
 */
export function buildAutoLoginUrl(
  target: string,
  dashboardUrl: string,
  options?: { prompt?: string }
): string {
  const targetUrl = new URL(target);
  if (targetUrl.origin !== new URL(dashboardUrl).origin) {
    return targetUrl.toString();
  }

  const loginUrl = new URL("/login", targetUrl.origin);
  loginUrl.searchParams.set(AUTO_LOGIN_PARAM, "true");
  loginUrl.searchParams.set(RETURN_TO_PARAM, targetUrl.toString());
  if (options?.prompt) {
    loginUrl.searchParams.set(PROMPT_PARAM, options.prompt);
  }
  return loginUrl.toString();
}

// Auth pages are never valid post-login destinations: a return_to pointing
// back at them would redirect the login route to itself in an endless loop.
const AUTH_PAGE_PATHS = new Set(["/login", "/register"]);
const TRAILING_SLASHES = /\/+$/;

function isAuthPagePath(pathname: string): boolean {
  const normalizedPath = pathname.replace(TRAILING_SLASHES, "") || "/";

  return (
    AUTH_PAGE_PATHS.has(normalizedPath) ||
    [...AUTH_PAGE_PATHS].some((path) => normalizedPath.startsWith(`${path}/`))
  );
}

/**
 * Validate a post-login redirect target coming from a query param. Only
 * same-origin URLs outside the auth pages are honored; anything else falls
 * back to the origin's root so the param can become neither an open redirect
 * nor a redirect loop.
 */
export function getSafeReturnTo(
  returnTo: string | null | undefined,
  origin: string
): string {
  const fallbackUrl = new URL("/", origin).toString();
  if (!returnTo) {
    return fallbackUrl;
  }
  try {
    const parsed = new URL(returnTo, origin);
    if (parsed.origin !== origin || isAuthPagePath(parsed.pathname)) {
      return fallbackUrl;
    }
    return parsed.toString();
  } catch {
    return fallbackUrl;
  }
}
